import React, { useState, useEffect } from "react";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import { InputOutput } from "../../InputOutput";
import Variables from "@/workflow_api/components/variables";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import { Button, Icon } from "@elaniintc/goes-design-system";
import { serializePaymentNode, validatePaymentData } from "./serialization";
import { PaymentNodeProvider, usePaymentNodeContext } from "./context";
import Content from "./content";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";

const PaymentNodeModal = () => {
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const { getNodeById, flowId } = useWorkflowStore();

  const {
    nodeDetailsOpen,
    setNodeDetailsOpen,
    setSelectedNodeId,
    selectedNodeId,
  } = useUIStore();
  const { variables: nodeVariables, isLoading: isLoadingVariables } =
    useGetVariables();
  const { mutateAsync: updateNode, isPending: isLoadingUpdate } =
    useUpdateNode(flowId);

  const node = getNodeById(selectedNodeId || "");
  const nodeData = node?.data as unknown as NodeData;
  const [nodeTitle, setNodeTitle] = useState<string>(
    nodeData?.displayName || "",
  );
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const [hasInteractedWithTitle, setHasInteractedWithTitle] = useState(false);
  const { getNameValidationError, isNameValid } =
    useNodeNameValidation(selectedNodeId);
  const [manualConfirmation, setManualConfirmation] = useState(false);

  // Inicializar manualConfirmation desde el backend
  useEffect(() => {
    if (nodeData?.manual_continue !== undefined) {
      setManualConfirmation(nodeData.manual_continue);
    }
  }, [nodeData?.manual_continue]);

  const {
    payment_title,
    setValidationEnabled,
    description,
    paymentItems,
    canEximp: paymentRequired,
    express,
  } = usePaymentNodeContext();

  const variableName = "resultado_pago"; // TODO: hacer dinámico

  const saveNode = async () => {
    if (!selectedNodeId || !node) return;

    // Activar validaciones al intentar guardar
    setValidationEnabled(true);

    // Verificar si el formulario es válido antes de guardar
    if (!validatePaymentData(payment_title, paymentItems)) {
      return false; // Indicar que no se pudo guardar
    }

    try {
      const serializedPaymentNode = serializePaymentNode(
        nodeData,
        {
          title: payment_title,
          description,
          paymentRequired,
          express,
          paymentItems,
        },
        nodeVariables,
      );

      await updateNode({
        node_id: selectedNodeId,
        node: {
          display_name: nodeTitle,
          flow_id: flowId,
          order: nodeData.order,
          position_x: node.position.x,
          position_y: node.position.y,
          ref_name: serializedPaymentNode.ref_name || "payment-node",
          type: serializedPaymentNode.type || "PAYMENT",
          manual_continue: manualConfirmation,
          inputs: serializedPaymentNode.inputs || [],
        },
      });

      return true; // Indicar éxito
    } catch (error) {
      console.error("Error saving payment node:", error);
      return false; // Indicar error
    }
  };

  const isFormValid = (): boolean => {
    const hasValidNodeTitle = nodeTitle.trim() !== "" && isNameValid(nodeTitle);
    const hasValidPaymentData = validatePaymentData(
      payment_title,
      paymentItems,
    );
    return hasValidNodeTitle && hasValidPaymentData;
  };

  const getNodeTitleError = (): string | undefined => {
    return getNameValidationError(nodeTitle);
  };

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        isSaveValid={isFormValid()}
        onClose={() => {
          setModalState("close-warning");
        }}
        onSave={async () => {
          setShowNodeTitleValidation(true);

          const success = await saveNode();
          if (success) {
            setNodeDetailsOpen(false);
            setSelectedNodeId();
          }
        }}
        isLoading={isLoadingUpdate}
      >
        <ThreeColumnLayout
          className="overflow-auto"
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
            hideSearch: true,
          }}
          centerHeader={{
            icon: "dollar-sign",
            title: nodeTitle,
            color: "warning",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setNodeTitle(newTitle);
              if (!hasInteractedWithTitle) {
                setHasInteractedWithTitle(true);
              }
              if (
                newTitle.trim() === "" &&
                (hasInteractedWithTitle || nodeTitle.trim() !== "")
              ) {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => {
              if (hasInteractedWithTitle) {
                setShowNodeTitleValidation(true);
              }
            },
            validationError: getNodeTitleError(),
            showValidation: showNodeTitleValidation,
            manualConfirmation,
            onManualConfirmationChange: setManualConfirmation,
            rightContent: (
              <Button
                variant="quiet"
                size="sm"
                className="text-body-loud bg-neutral-object border border-neutral-quiet hover:bg-neutral-object-accent-silent"
                onClick={() => {
                  // TODO: Implementar funcionalidad de test de pago
                }}
              >
                Test
              </Button>
            ),
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
                <Variables sourceNodeId={selectedNodeId!} />
              </div>
            </>
          }
          leftContentWidth="min-w-[445px]"
          centerContent={
            !isLoadingVariables ? (
              <Content variables={nodeVariables} />
            ) : (
              <div className="flex justify-center items-center py-12">
                <Icon
                  type="far"
                  name="spinner-third"
                  className="animate-spin text-xl text-body"
                />
              </div>
            )
          }
          rightContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
                <InputOutput
                  nodeType={NodeTypes.PAYMENT}
                  showSteps={false}
                  nodeLabel={nodeTitle}
                  variableName={variableName}
                  variableType="object"
                />
              </div>
            </>
          }
        />
      </FullModal>
    </>
  );
};

export const PaymentNodeDetails: React.FC = () => {
  const { getNodeById } = useWorkflowStore();
  const { selectedNodeId } = useUIStore();
  const selectedNode = getNodeById(selectedNodeId || "");
  const nodeData = selectedNode?.data as unknown as NodeData;
  return (
    <PaymentNodeProvider data={nodeData}>
      <PaymentNodeModal />
    </PaymentNodeProvider>
  );
};
